import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import url from '../../constants/config';
import notFound from '../../assets/notFound.png';
import Loader from '../../shared/displayComponents/loader/Loader';
import { removeFromCompare, clearCompare, selectCompareIds } from '../../shared/store/compareSlice';
import {
  addToFavourites,
  deleteFromFavourites,
} from '../../shared/store/authSlice';

import './ComparePage.scss';

const getPhoto = p => p.imageBase64 || notFound;

const dash = val => {
  if (val === null || val === undefined || val === '') return '—';
  if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
  return val;
};

/**
 * Compare Page — side-by-side comparison of 2–4 players.
 *
 * Compared fields (v1):
 *   Basic: name, sport, gender, availableFrom, position, height, weight, attackReach
 *   Academics: GPA, SAT, TOEFL, Duolingo, intendedMajor
 *   School: currentSchool, currentClub, budget, transfer
 *   Evaluation: levelOfPlay, englishSkills, physicals, analysis (top 3)
 *   Media: youtubeUrl count + links
 *   Accomplishments: top 3
 */
const ComparePage = () => {
  const compareIds = useSelector(selectCompareIds);
  const { userEmail, favouritesPlayers } = useSelector(
    ({ authorization }) => authorization,
  );
  const dispatch = useDispatch();
  const history = useHistory();

  const [players, setPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (compareIds.length < 2) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const fetchCompare = async () => {
      try {
        const token = window.localStorage.getItem('authorization');
        const { data } = await axios.post(
          `${url}/coach/compare`,
          { playerIds: compareIds },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!cancelled) setPlayers(data.players || []);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              'Failed to load comparison data.',
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchCompare();
    return () => { cancelled = true; };
  }, [compareIds]);

  const handleRemove = id => {
    dispatch(removeFromCompare(id));
  };

  const handleClearAll = () => {
    dispatch(clearCompare());
    history.push('/available-players');
  };

  const handleFavToggle = id => {
    if ((favouritesPlayers || []).includes(id)) {
      dispatch(deleteFromFavourites(userEmail, id));
    } else {
      dispatch(addToFavourites(userEmail, id));
    }
  };

  // ── Not enough players ──────────────────────────────────────
  if (!isLoading && compareIds.length < 2) {
    return (
      <div className="compare-page">
        <div className="compare-page__empty">
          <div className="compare-page__empty-icon">
            <i className="far fa-clone" />
          </div>
          <h3>Not enough players to compare</h3>
          <p>
            Select at least 2 players from the player list to compare them
            side-by-side.
          </p>
          <button
            type="button"
            className="compare-page__cta"
            onClick={() => history.push('/available-players')}
          >
            <i className="fas fa-search" /> Browse Players
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="compare-page">
        <div className="compare-page__header">
          <h2 className="compare-page__title">
            <i className="fas fa-columns" /> Player Comparison
          </h2>
        </div>
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="compare-page">
        <div className="compare-page__header">
          <h2 className="compare-page__title">
            <i className="fas fa-columns" /> Player Comparison
          </h2>
        </div>
        <div className="compare-page__empty">
          <i className="fas fa-exclamation-circle" style={{ fontSize: '2rem', color: '#cc3d45', marginBottom: '1rem' }} />
          <h3>{error}</h3>
          <button
            type="button"
            className="compare-page__cta"
            onClick={() => history.push('/available-players')}
          >
            Back to Players
          </button>
        </div>
      </div>
    );
  }

  const colCount = players.length;

  // Row definitions — each row is { label, icon?, render(player) }
  const sections = [
    {
      title: 'Basic Information',
      icon: 'fa-user',
      rows: [
        { label: 'Sport', render: p => dash(p.sport) },
        { label: 'Gender', render: p => dash(p.gender) },
        { label: 'Available From', render: p => dash(p.graduationYear) },
        { label: 'Position', render: p => dash(p.position) },
        { label: 'Height', render: p => dash(p.height) },
        { label: 'Weight', render: p => dash(p.weight) },
        { label: 'Attack Reach', render: p => dash(p.attackReach) },
        { label: 'Nationality', render: p => dash(p.nationality) },
        { label: 'Date of Birth', render: p => {
          if (!p.dateOfBirth) return '—';
          const [y, m, d] = p.dateOfBirth.split('-');
          return `${m}-${d}-${y}`;
        }},
      ],
    },
    {
      title: 'Academics',
      icon: 'fa-graduation-cap',
      rows: [
        { label: 'GPA', render: p => dash(p.highSchoolGpa) },
        { label: 'SAT', render: p => dash(p.satScore) },
        { label: 'TOEFL', render: p => dash(p.toeflScore) },
        { label: 'Duolingo', render: p => dash(p.duolingoScore) },
        { label: 'Intended Major', render: p => dash(p.intendedMajor) },
      ],
    },
    {
      title: 'School & Club',
      icon: 'fa-school',
      rows: [
        { label: 'School / University', render: p => dash(p.currentSchool) },
        { label: 'Current Club', render: p => dash(p.currentClub) },
        { label: 'Budget', render: p => dash(p.budget) },
        { label: 'Transfer', render: p => (p.transfer ? 'Yes' : 'No') },
      ],
    },
    {
      title: 'Player Evaluation',
      icon: 'fa-star',
      rows: [
        { label: 'Level of Play', render: p => dash(p.levelOfPlay) },
        { label: 'English Skills', render: p => dash(p.englishSkills) },
        {
          label: 'Physical Traits',
          render: p => {
            if (!p.physicals?.length) return '—';
            return p.physicals.join(', ');
          },
        },
        {
          label: 'In-Person Analysis',
          render: p => {
            if (!p.analysis?.length) return '—';
            return p.analysis.join('; ');
          },
        },
      ],
    },
    {
      title: 'Accomplishments',
      icon: 'fa-trophy',
      rows: [
        {
          label: 'Accomplishments',
          render: p => {
            if (!p.accomplishments?.length) return '—';
            return p.accomplishments.join('; ');
          },
        },
      ],
    },
  ];

  return (
    <div className="compare-page">
      <div className="compare-page__header">
        <div>
          <h2 className="compare-page__title">
            <i className="fas fa-columns" /> Player Comparison
          </h2>
          <p className="compare-page__subtitle">
            Comparing {colCount} players side by side
          </p>
        </div>
        <div className="compare-page__header-actions">
          <button
            type="button"
            className="compare-page__back"
            onClick={() => history.goBack()}
          >
            <i className="fas fa-arrow-left" /> Back
          </button>
          <button
            type="button"
            className="compare-page__clear-btn"
            onClick={handleClearAll}
          >
            <i className="fas fa-times" /> Clear All
          </button>
        </div>
      </div>

      <div className="compare-table-wrapper">
        <table className={`compare-table compare-table--cols-${colCount}`}>
          {/* ── Header: photos + names ── */}
          <thead>
            <tr className="compare-table__photo-row">
              <th className="compare-table__label-cell" />
              {players.map(p => (
                <th key={p._id} className="compare-table__player-header">
                  <button
                    type="button"
                    className="compare-table__remove"
                    onClick={() => handleRemove(p._id)}
                    title="Remove from comparison"
                  >
                    <i className="fas fa-times" />
                  </button>
                  <img
                    src={getPhoto(p)}
                    alt={`${p.firstName} ${p.lastName}`}
                    className="compare-table__photo"
                    onClick={() => history.push({ pathname: `/available-players/${p._id}`, state: { source: 'compare' } })}
                  />
                  <h3
                    className="compare-table__name"
                    onClick={() => history.push({ pathname: `/available-players/${p._id}`, state: { source: 'compare' } })}
                  >
                    {p.firstName} {p.lastName}
                    {p.transfer && <span className="compare-table__badge">Transfer</span>}
                  </h3>
                  <div className="compare-table__header-actions">
                    <button
                      type="button"
                      className="compare-table__action"
                      onClick={() => history.push({ pathname: `/available-players/${p._id}`, state: { source: 'compare' } })}
                    >
                      View Profile
                    </button>
                    <button
                      type="button"
                      className={`compare-table__action compare-table__action--fav ${
                        (favouritesPlayers || []).includes(p._id) ? 'compare-table__action--fav-active' : ''
                      }`}
                      onClick={() => handleFavToggle(p._id)}
                      title={(favouritesPlayers || []).includes(p._id) ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <i className={(favouritesPlayers || []).includes(p._id) ? 'fas fa-heart' : 'far fa-heart'} />
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* ── Body: data rows ── */}
          <tbody>
            {sections.map(section => (
              <React.Fragment key={section.title}>
                <tr className="compare-table__section-row">
                  <td colSpan={colCount + 1} className="compare-table__section-title">
                    <i className={`fas ${section.icon}`} /> {section.title}
                  </td>
                </tr>
                {section.rows.map(row => (
                  <tr key={row.label} className="compare-table__data-row">
                    <td className="compare-table__label-cell">{row.label}</td>
                    {players.map(p => (
                      <td key={p._id} className="compare-table__value-cell">
                        {row.render(p)}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComparePage;
